import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, ArrowRight, ShieldCheck, Search, Star,
  Mail, ClipboardList, Sparkles, Quote,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const B = '/brand/ctg';

const BOOKS = [
  { img: `${B}/book-management-development.png`, title: 'Management Development for Care & Nursing Home Managers',
    fore: 'Foreword: Dr Terry Tucker, Distinguished Professor of Healthcare', rating: '4.4 from 69 Amazon reviews · also on audiobook' },
  { img: `${B}/book-leadership-secrets.png`, title: 'Leadership Secrets for Care Home Managers',
    fore: 'Foreword: Barry Stanley-Wilkinson, former CQC lawyer & co-creator of the CQC regulations', rating: '4.0 from 46 Amazon reviews' },
  { img: `${B}/book-cqc-manager.png`, title: 'So You Want to Be a CQC Registered Care Home Manager?',
    fore: 'Foreword: Dr Umesh Prabhu, former NHS Medical Director', rating: '4.2 from 63 Amazon reviews' },
];

const PODCASTS = [
  { img: `${B}/podcast-care-home-quality.png`, title: 'Care Home Quality: Meet the Leaders & Innovators',
    blurb: 'C-suite leaders, operators and innovators. 25 guests, 20,000 downloads, listeners in over 100 countries.' },
  { img: `${B}/podcast-meet-suppliers.png`, title: 'Meet the CareTech Suppliers',
    blurb: 'Founders and suppliers selling into care, in association with CareTechGuide. 10 supplier interviews to date.' },
];

const FAQS = [
  { q: 'What is all-in-one homecare software?',
    a: 'All-in-one homecare software combines scheduling, care planning, electronic visit verification (EVV), billing, and family communication into a single system, rather than requiring separate tools that don’t share data.' },
  { q: 'Do you sell or recommend a specific homecare software product?',
    a: 'No. I don’t sell software and I’m not paid by any supplier to recommend them. My job is to help you work out what you need and point you to the couple of options that actually fit - the decision is always yours.' },
  { q: 'Is the assessment really free?',
    a: 'Yes. The assessment and tailored recommendation have no cost and no obligation.' },
  { q: 'How is this different from a homecare software comparison website?',
    a: 'This isn’t a platform or a list - it’s direct, independent advice from someone who has actually managed care services, implemented a rostering system, and lived with the gap between what suppliers promise and what actually happens.' },
  { q: 'How many people use CareTechGuide?',
    a: 'Around 15,000 care providers a year use CareTechGuide to help them choose homecare technology.' },
];

const field = 'w-full px-4 py-3 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40 focus:border-[#00A79D]';

const SignupForm = ({ requestType, onDone, cta, note, testidPrefix, dark }) => {
  const [form, setForm] = useState({ name: '', email: '', organisation: '' });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error('Please enter your email'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/buying-help/signup`, {
        name: form.name.trim() || null, email: form.email.trim(),
        organisation: form.organisation.trim() || null, request_type: requestType,
        ref: document.referrer || null,
        utm: new URLSearchParams(window.location.search).get('utm_source') || null,
      });
      if (requestType === 'assessment' && r.data?.survey_url) {
        const token = r.data.survey_url.split('/assessment/')[1];
        toast.success('Great - let’s build your recommendation.');
        navigate(`/homecare-buying-help/assessment/${token}`);
        return;
      }
      toast.success('On its way to your inbox.');
      onDone && onDone();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testidPrefix}-form`}>
      <input type="text" placeholder="Your name" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid={`${testidPrefix}-name`} />
      <input type="text" placeholder="Your organisation (optional)" value={form.organisation}
        onChange={(e) => setForm({ ...form, organisation: e.target.value })} className={field} data-testid={`${testidPrefix}-org`} />
      <input type="email" required placeholder="Work email" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid={`${testidPrefix}-email`} />
      <button type="submit" disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60"
        data-testid={`${testidPrefix}-submit`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {cta}
      </button>
      {note && <p className={`text-xs ${dark ? 'text-gray-300' : 'text-gray-500'} text-center`}>{note}</p>}
    </form>
  );
};

export default function BuyingHelp() {
  const formRef = useRef(null);
  const [factsheetDone, setFactsheetDone] = useState(false);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  useEffect(() => { document.title = 'Free Homecare Software Buying Help - CareTechGuide'; }, []);

  return (
    <div className="ctg-public min-h-screen bg-white text-gray-900" data-testid="page-buying-help">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/brand/caretechcompare-logo.png" alt="Care Tech Compare" className="h-9" data-testid="bh-logo" />
          <button onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors"
            data-testid="bh-header-cta">
            Get your free recommendation
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0B2942] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 text-white px-3 py-1 rounded-full mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Free, independent - no sales pitch
            </span>
            <h1 className="font-bold tracking-tight text-4xl sm:text-5xl leading-[1.1] text-white">
              Choosing an all-in-one homecare system? <span className="text-[#4fd1c5]">I’ll help you get it right.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed">
              I’m a former CQC registered manager. I’ve implemented a rostering system myself - and lived the gap
              between what the salesperson promises (“it’ll save you time”) and what actually happens. For the last
              three years I’ve run CareTechGuide, a free independent resource around 15,000 care providers use every
              year. By popular request, we now offer a free assessment and recommendation service too.
            </p>
            <button onClick={scrollToForm}
              className="mt-8 inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-7 py-4 rounded-md transition-colors text-base"
              data-testid="bh-hero-cta">
              Get your free tailored recommendation <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          <div>
            <img src={`${B}/hero-care-manager-ctc.jpg`} alt="Care manager choosing a system"
              className="rounded-xl shadow-2xl w-full" data-testid="bh-hero-image" />
          </div>
        </div>
      </section>

      {/* How I help */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-bold text-3xl text-[#0B2942]">
              A few questions. A shortlist that actually fits.
            </h2>
            <p className="mt-4 text-gray-600">Before you talk to a single supplier, it’s worth working out:</p>
            <ul className="mt-5 space-y-3">
              {[
                'What’s actually going wrong with what you have now - or what to avoid, if this is your first system.',
                'What “all-in-one” needs to mean for your agency specifically - EVV, family app, scheduling, billing, compliance reporting.',
                'Which couple of systems are genuinely worth your time - and why the rest aren’t.',
                'Whether you’re even ready to buy, or should fix something else first.',
              ].map((t) => (
                <li key={t} className="flex gap-3 text-gray-700"><CheckCircle2 className="w-5 h-5 text-[#00A79D] flex-shrink-0 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
            <p className="mt-6 text-sm font-medium text-[#0B2942]">No pitch, no obligation - just what actually fits your service.</p>
          </div>
          <div className="bg-[#F9FAFB] border border-gray-200 rounded-xl p-7">
            <h3 className="font-semibold text-xl text-[#0B2942]">What you get</h3>
            <p className="text-sm text-gray-500 mb-4">Not a sales conversation. Insider knowledge on your side.</p>
            <ul className="space-y-3">
              {[
                'We know the suppliers - and the people who actually work there.',
                'The stuff you won’t find on a comparison site or in a brochure.',
                'A shortlist narrowed to the couple of options that’ll genuinely suit your service.',
                'An honest read on whether now’s even the right time to switch or buy.',
              ].map((t) => (
                <li key={t} className="flex gap-3 text-gray-700 text-sm"><Star className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Sign-up: assessment + factsheet */}
      <section ref={formRef} className="bg-[#0B2942] text-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white text-gray-900 rounded-xl p-7 shadow-xl border-t-4 border-[#00A79D] h-full flex flex-col">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-3 self-start">
              <Sparkles className="w-3.5 h-3.5" /> Recommended
            </span>
            <h3 className="font-bold text-2xl bh-card-title">Get your free tailored recommendation</h3>
            <p className="text-sm text-gray-600 mt-1 mb-5">Answer 8 quick questions (about 7 minutes) and I’ll build you an independent shortlist.</p>
            <div className="mt-auto">
              <SignupForm requestType="assessment" cta="Start my free assessment" testidPrefix="bh-assessment"
                note="No cost, no obligation. Your details are never sold." />
            </div>
          </div>

          <div className="bg-white/5 border border-white/15 rounded-xl p-7 h-full flex flex-col">
            <h3 className="font-semibold text-xl flex items-center gap-2 text-white"><ClipboardList className="w-5 h-5 text-[#4fd1c5]" /> Not ready for the full assessment?</h3>
            <p className="text-sm text-gray-300 mt-1 mb-4">Get the factsheet - the key points to help you choose, sent straight to your inbox.</p>
            {factsheetDone ? (
              <div className="bg-white/10 rounded-md p-5 text-center" data-testid="bh-factsheet-success">
                <CheckCircle2 className="w-8 h-8 text-[#4fd1c5] mx-auto mb-2" />
                <p className="font-semibold text-white">Sent - check your inbox.</p>
                <p className="text-sm text-gray-300 mt-1">You’ll also get a short, useful email series from me.</p>
              </div>
            ) : (
              <SignupForm requestType="factsheet" cta="Get the factsheet" testidPrefix="bh-factsheet" dark
                onDone={() => setFactsheetDone(true)} />
            )}
            <div className="mt-auto pt-6 border-t border-white/15 text-sm text-gray-300 flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-[#4fd1c5] flex-shrink-0" />
              <span>Got a specific question? Email me directly:{' '}
                <a href="mailto:liam@caretechguide.co.uk" className="text-[#4fd1c5] font-medium underline">liam@caretechguide.co.uk</a></span>
            </div>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-bold text-3xl text-[#0B2942] text-center">This is for you if…</h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {[
            'You’re choosing a homecare system for the first time and don’t know what “good” looks like.',
            'You’re switching providers and want to avoid repeating the same mistakes.',
            'You’ve had a bad experience with a supplier and want someone in your corner this time.',
            'You just want a second opinion before you sign anything.',
          ].map((t) => (
            <div key={t} className="flex gap-3 bg-[#F9FAFB] border border-gray-200 rounded-lg p-5 text-gray-700">
              <CheckCircle2 className="w-5 h-5 text-[#00A79D] flex-shrink-0 mt-0.5" /><span>{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* About me */}
      <section className="bg-[#F9FAFB] border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-1">
            <img src={`${B}/liam-headshot.png`} alt="Liam Palmer" className="rounded-xl shadow-lg w-full max-w-xs" data-testid="bh-headshot" />
            <p className="mt-4 font-semibold text-[#0B2942] text-lg">Liam Palmer</p>
            <p className="text-sm text-gray-500">Founder, CareTechGuide</p>
          </div>
          <div className="lg:col-span-2">
            <h2 className="font-bold text-3xl text-[#0B2942]">A bit about me</h2>
            <p className="mt-4 text-gray-700 leading-relaxed">
              Former CQC registered manager. Thirteen years senior management across private hospitals, retirement
              villages, care homes, and mental health services - including implementing a rostering system myself,
              and learning the hard way what the sales pitch doesn’t tell you. For the last three years I’ve run
              CareTechGuide, a free independent resource around 15,000 care providers use every year.
            </p>
            <div className="mt-6 flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4 text-gray-700">
              <Quote className="w-5 h-5 text-[#00A79D] flex-shrink-0 mt-0.5" />
              <span className="text-sm">I’ve lived the gap between what the salesperson promises and what actually happens on the floor. That’s the difference here.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Books */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-bold text-3xl text-[#0B2942] text-center">Published books</h2>
        <div className="mt-10 grid sm:grid-cols-3 gap-8">
          {BOOKS.map((b) => (
            <div key={b.title} className="text-center" data-testid="bh-book">
              <img src={b.img} alt={b.title} className="h-64 mx-auto rounded-md shadow-lg object-contain" />
              <p className="mt-4 font-semibold text-[#0B2942] text-sm leading-snug">{b.title}</p>
              <p className="mt-1 text-xs text-gray-500">{b.fore}</p>
              <p className="mt-1 text-xs font-medium text-[#F59E0B]">{b.rating}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Podcasts */}
      <section className="bg-[#F9FAFB] border-y border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-bold text-3xl text-[#0B2942] text-center">Podcasts</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-8">
            {PODCASTS.map((p) => (
              <div key={p.title} className="flex gap-5 bg-white border border-gray-200 rounded-xl p-5" data-testid="bh-podcast">
                <img src={p.img} alt={p.title} className="w-24 h-24 rounded-md object-contain flex-shrink-0" />
                <div>
                  <p className="font-semibold text-[#0B2942] leading-snug">{p.title}</p>
                  <p className="mt-2 text-sm text-gray-600">{p.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-bold text-3xl text-[#0B2942] text-center mb-8">Common questions</h2>
        <div className="space-y-4">
          {FAQS.map((f) => (
            <div key={f.q} className="border border-gray-200 rounded-lg p-5" data-testid="bh-faq">
              <p className="font-semibold text-[#0B2942] flex items-start gap-2"><Search className="w-4 h-4 text-[#00A79D] mt-1 flex-shrink-0" />{f.q}</p>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed pl-6">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <button onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-7 py-4 rounded-md transition-colors"
            data-testid="bh-faq-cta">
            Get your free tailored recommendation <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <footer className="bg-[#0B2942] text-gray-300 text-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <img src="/brand/ctc-mark.png" alt="Care Tech Compare" className="h-8 w-auto" />
            <span className="font-bold text-white text-lg lowercase tracking-tight">care tech compare</span>
          </div>
          <p>© {new Date().getFullYear()} Care Tech Compare · Free, independent advice spanning the whole market.</p>
        </div>
      </footer>
    </div>
  );
}
